import { Helmet } from 'react-helmet-async';
import { ArrowRight, Lock, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { FORM_WELLBEING_PORTRAIT_CTA_CLASS } from './FormWellbeingPortraitMock';
// Mantido comentado intencionalmente: tela exibida quando a análise não retorna dados.
// Voltar a usar quando o endpoint estiver estável; por ora, exibimos a tela "em breve".
// import { FormEnergyDimensionsAnalyticsEmpty } from './FormEnergyDimensionsAnalyticsEmpty';
import { FormEnergyDimensionsAnalyticsComingSoon } from './FormEnergyDimensionsAnalyticsComingSoon';
import { PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY } from '@/constants/session';
import { useGetMyFormAnswersAnalytics } from '@/services/queries/form';

const DECLINE_OUTLINE_CTA_CLASS =
	'min-w-[200px] justify-center rounded-full border-2 border-zinc-500 bg-transparent px-8 py-3 text-sm font-bold text-zinc-500 shadow-none transition-colors duration-200 hover:border-zinc-600 hover:bg-zinc-50/80 hover:text-zinc-600 active:scale-[0.98]';

const LIKERT_MIN = 1;
const LIKERT_MAX = 5;

/**
 * Compatibilidade entre payloads:
 * - média em 1..5 (escala Likert antiga) -> normaliza para 0..100
 * - média em 0..100 (payload atual) -> usa direto
 */
function normalizeAverageTo100(average: number): number {
	if (average >= LIKERT_MIN && average <= LIKERT_MAX) {
		const span = LIKERT_MAX - LIKERT_MIN;
		if (span <= 0) return 0;
		return Math.round(((average - LIKERT_MIN) / span) * 100);
	}

	return Math.round(Math.min(100, Math.max(0, average)));
}

const GIBBERISH_LINES = [
	'Kzzpt-Ω: vlim noot 7.404 — framb não correlata.',
	'Mox flarble :: qv99 → thomp. (sem série, sem uso).',
	'Blorp-snarp: ___não é texto de saúde___ / void.',
	'Zim-zam tabula rasa [placeholder Plumaa-teste].',
	'Quorxn v8 :: lorem ipsum invertido — ignorar.',
] as const;

function gibberishForId(seed: string): {
	title: string;
	score: number;
	max: number;
	description: string;
	tip: string;
} {
	let h = 0;
	for (let i = 0; i < seed.length; i += 1) {
		h = (h * 31 + seed.charCodeAt(i)) | 0;
	}
	const pick = (arr: readonly string[]) =>
		arr[Math.abs(h) % arr.length] ?? arr[0];
	return {
		title: pick(['Dimensão Qx-β', 'Bloco flim-9', 'Área zorb-null']),
		score: Math.abs(h) % 97,
		max: 100,
		description: pick(GIBBERISH_LINES),
		tip: pick([...GIBBERISH_LINES].reverse()),
	};
}

function mapCbiCategoriesToEnergyDimensions(
	categories: Forms.FormMyAnalyticsCategoryRow[],
): EnergyDimensionMockItem[] {
	return categories.map((cat) => {
		if (cat.is_anonymous === true) {
			const g = gibberishForId(cat.category_id);
			return {
				id: cat.category_id,
				title: g.title,
				score: g.score,
				max: g.max,
				isPositiveImpact: null,
				description: g.description,
				tip: g.tip,
				cardLocked: true,
			};
		}

		const avg = cat.average ?? 0;
		const normalizedScore = normalizeAverageTo100(avg);
		const score = normalizedScore;
		const tipDetailed = cat.tip?.detailed_tip?.trim() || '';
		const tipShort = cat.tip?.short_tip?.trim() || '';
		const descText =
			tipShort ||
			(cat.name
				? `Resultado agregado da dimensão «${cat.name}».`
				: 'Resultado agregado desta dimensão.');
		const tipBody = tipDetailed || tipShort || '';

		return {
			id: cat.category_id,
			title: cat.name?.trim() || 'Dimensão',
			score,
			max: 100,
			isPositiveImpact: cat.is_positive_impact,
			description: descText,
			tip: tipBody || '—',
			cardLocked: false,
		};
	});
}

export type EnergyDimensionBand = 'alert' | 'attention' | 'strong';

export type EnergyDimensionMockItem = {
	id: string;
	title: string;
	score: number;
	max: number;
	isPositiveImpact?: boolean | null;
	description: string;
	tip: string;
	cardLocked?: boolean;
};

export type FormEnergyDimensionsMockData = {
	eyebrow: string;
	titleHighlight: string;
	titleRest: string;
	subtitle: string;
	dimensions: EnergyDimensionMockItem[];
	unlockPlanCtaLabel: string;
	declineCtaLead: string;
	declineCtaLabel: string;
};

export const FORM_ENERGY_DIMENSIONS_DEFAULTS: FormEnergyDimensionsMockData = {
	eyebrow: 'Dimensões de energia',
	titleHighlight: '3 Dimensões',
	titleRest: ' de Energia',
	subtitle: 'Com base no questionário de bem-estar',
	unlockPlanCtaLabel: 'Desbloquear meu Plano Completo',
	declineCtaLead:
		'Com o plano completo, você passa a ter um caminho mais claro para lidar com o que apareceu nas suas respostas — indo além de só enxergar o retrato geral, e realmente avançando aos poucos.',
	declineCtaLabel: 'Não quero desbloquear agora',
	dimensions: [],
};

const scorePercent = (value: number, max: number) => {
	if (max <= 0) return 0;
	return Math.min(100, Math.max(0, (value / max) * 100));
};

const LOW_MAX = 49;
const MODERATE_MAX = 74;

const bandFromPercent = (
	pct: number,
	isPositiveImpact: boolean | null | undefined,
): EnergyDimensionBand => {
	if (isPositiveImpact === false) {
		if (pct <= LOW_MAX) return 'strong';
		if (pct <= MODERATE_MAX) return 'attention';
		return 'alert';
	}

	if (pct <= LOW_MAX) return 'alert';
	if (pct <= MODERATE_MAX) return 'attention';
	return 'strong';
};

const bandStyles = (band: EnergyDimensionBand) => {
	switch (band) {
		case 'alert':
			return {
				badge: 'border-red-200 bg-red-50 text-red-800',
				label: 'Alerta',
				bar: 'bg-red-500',
			};
		case 'attention':
			return {
				badge: 'border-amber-200 bg-amber-50 text-amber-900',
				label: 'Atenção',
				bar: 'bg-amber-500',
			};
		default:
			return {
				badge: 'border-emerald-200 bg-emerald-50 text-emerald-900',
				label: 'Fortalecido',
				bar: 'bg-emerald-500',
			};
	}
};

function DimensionLockedOverlay() {
	const handleUnlockClick = () => {
		try {
			sessionStorage.setItem(PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY, '1');
		} catch {
			/* ignore */
		}
	};

	return (
		<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-brand-orange/20 px-3 py-6 backdrop-blur-lg sm:px-4">
			<div className="flex max-w-[min(100%,20rem)] flex-col items-center justify-center gap-4 sm:max-w-[min(100%,28rem)] sm:gap-5">
				<Lock
					className="size-6 shrink-0 text-brand-orange sm:size-7"
					strokeWidth={2.5}
					aria-hidden
				/>
				<div className="w-full text-center">
					<Link
						to="/clubes"
						onClick={handleUnlockClick}
						className="pointer-events-auto inline-block max-w-full text-sm font-semibold leading-snug text-brand-orange transition-colors hover:text-brand-orange"
					>
						<span className="underline decoration-brand-orange/60 underline-offset-[4px] transition-[text-decoration-color] hover:decoration-brand-orange">
							No plano Lite você pode desbloquear esta dimensão
						</span>
						<ArrowRight
							className="ml-0.5 inline-block size-4 align-middle text-brand-orange"
							strokeWidth={2.5}
							aria-hidden
						/>
					</Link>
				</div>
			</div>
		</div>
	);
}

function EnergyDimensionTipBlock({
	tip,
	index,
	tipHeading,
}: {
	tip: string;
	index: number;
	tipHeading: string;
}) {
	if (!tip || tip === '—') return null;

	return (
		<div className="mt-4 rounded-2xl border border-brand-orange/20 bg-brand-orange/5 px-4 py-4 md:px-5">
			<p className="text-[11px] font-black uppercase tracking-wide text-brand-orange">
				{`Dica ${index + 1} - ${tipHeading}`}
			</p>
			<p className="mt-2 text-sm leading-snug text-zinc-900 md:text-[0.95rem]">
				{tip}
			</p>
		</div>
	);
}

function tipHeadingFromTitle(title: string): string {
	const t = title.trim();
	if (!t) return 'DIMENSÃO';
	return `ENERGIA — ${t.slice(0, 48).toUpperCase()}`;
}

type FormEnergyDimensionsMockProps = {
	data?: Partial<FormEnergyDimensionsMockData>;
	onDecline?: () => void;
	fetchEnabled?: boolean;
	declineRoute?: string;
};

const FormEnergyDimensionsMock = ({
	data,
	onDecline,
	fetchEnabled = true,
	declineRoute = '/home',
}: FormEnergyDimensionsMockProps) => {
	const useRemoteDimensions = data?.dimensions === undefined;

	const {
		data: analyticsRes,
		isLoading,
		isError,
		refetch,
	} = useGetMyFormAnswersAnalytics({
		enabled: fetchEnabled && useRemoteDimensions,
	});

	const cbi = analyticsRes?.data?.CBI ?? null;
	const analysis = cbi?.analysis_data ?? null;
	const categories = analysis?.categories ?? [];

	const derivedDimensions =
		categories.length > 0
			? mapCbiCategoriesToEnergyDimensions(categories)
			: [];

	const nDim = useRemoteDimensions
		? derivedDimensions.length
		: data?.dimensions?.length ?? 0;
	const titleHighlight =
		nDim === 1
			? '1 Dimensão'
			: nDim > 1
			  ? `${nDim} Dimensões`
			  : 'Dimensões';

	const merged: FormEnergyDimensionsMockData = {
		...FORM_ENERGY_DIMENSIONS_DEFAULTS,
		...data,
		titleHighlight: data?.titleHighlight ?? titleHighlight,
		subtitle:
			data?.subtitle ??
			(cbi?.form?.title?.trim()
				? `Com base no questionário: ${cbi.form.title.trim()}`
				: FORM_ENERGY_DIMENSIONS_DEFAULTS.subtitle),
		dimensions: useRemoteDimensions
			? derivedDimensions
			: data?.dimensions ?? [],
	};

	const {
		dimensions,
		unlockPlanCtaLabel,
		declineCtaLead,
		declineCtaLabel,
		...head
	} = merged;

	const showApiShell = useRemoteDimensions;

	return (
		<section className="relative flex min-h-full w-full flex-1 flex-col bg-white px-5 py-8 sm:px-8 md:py-12 lg:px-12 xl:px-16 2xl:px-20">
			<Helmet>
				<title>Suas dimensões de energia | Plumaa</title>
				<meta
					name="description"
					content="Veja como está sua energia pessoal, profissional e nas relações."
				/>
			</Helmet>

			<div className="flex w-full flex-1 flex-col">
				{showApiShell && isLoading ? (
					<div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4 py-16">
						<Loader2
							className="h-10 w-10 animate-spin text-zinc-400"
							aria-hidden
						/>
						<p className="text-sm text-zinc-500">
							Carregando suas dimensões…
						</p>
					</div>
				) : showApiShell && isError ? (
					<div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-4 py-16 text-center">
						<p className="text-sm text-zinc-600">
							Não foi possível carregar seus dados agora.
						</p>
						<button
							type="button"
							onClick={() => refetch()}
							className="rounded-full border border-zinc-300 px-4 py-2 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
						>
							Tentar de novo
						</button>
					</div>
				) : showApiShell &&
				  !isLoading &&
				  !isError &&
				  (!cbi || categories.length === 0) ? (
					// <FormEnergyDimensionsAnalyticsEmpty
					//   unlockPlanCtaLabel={unlockPlanCtaLabel}
					//   declineCtaLabel={declineCtaLabel}
					//   declineCtaLead={declineCtaLead}
					//   onDecline={onDecline}
					// />
					<FormEnergyDimensionsAnalyticsComingSoon />
				) : !showApiShell ||
				  (!isLoading && !isError && dimensions.length > 0) ? (
					<div className="mx-auto w-full max-w-lg sm:max-w-2xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl">
						<p className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">
							{head.eyebrow}
						</p>

						<h1 className="mt-5 text-center text-2xl font-semibold tracking-tight text-zinc-900 md:text-[1.65rem] lg:text-3xl">
							Suas{' '}
							<span className="text-brand-orange">
								{head.titleHighlight}
							</span>
							{head.titleRest}
						</h1>
						<p className="mt-2 text-center text-sm text-zinc-500 md:text-base">
							{head.subtitle}
						</p>

						<div className="mt-8 flex flex-col gap-4 lg:gap-5">
							{dimensions.map((dim, index) => {
								const pct = scorePercent(dim.score, dim.max);
								const band = bandFromPercent(
									pct,
									dim.isPositiveImpact,
								);
								const styles = bandStyles(band);
								const tipHeading = tipHeadingFromTitle(
									dim.title,
								);

								if (dim.cardLocked) {
									return (
										<article
											key={dim.id}
											className="relative overflow-hidden rounded-2xl border border-brand-orange bg-gradient-to-b from-brand-orange/5 to-transparent p-4 shadow-sm md:p-5"
										>
											<div
												className="pointer-events-none select-none blur-xl"
												aria-hidden
											>
												<div className="flex items-start justify-between gap-3">
													<h2 className="text-left text-base font-bold leading-snug text-zinc-900 md:text-lg">
														{dim.title}
													</h2>
													<span className="shrink-0 rounded-full border border-brand-orange/30 bg-brand-orange/10 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand-orange">
														Bloqueado
													</span>
												</div>

												<div className="mt-3 flex items-baseline gap-1">
													<span className="text-3xl font-black tabular-nums text-zinc-900">
														??
													</span>
													<span className="text-lg font-semibold tabular-nums text-zinc-400">
														/??
													</span>
												</div>

												<div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-200">
													<div className="h-full w-1/2 rounded-full bg-brand-orange/35" />
												</div>

												<p className="mt-3 text-left text-sm leading-relaxed text-zinc-600">
													{dim.description}
												</p>

												<div className="mt-4">
													<p className="text-[11px] font-black uppercase tracking-wide text-brand-orange">
														{`Dica ${
															index + 1
														} - ${tipHeading}`}
													</p>
													<p className="mt-2 text-sm leading-snug text-zinc-900 md:text-[0.95rem]">
														{dim.tip}
													</p>
												</div>
											</div>
											<DimensionLockedOverlay />
										</article>
									);
								}

								return (
									<article
										key={dim.id}
										className="rounded-2xl border border-zinc-200/80 bg-white/70 p-4 shadow-sm backdrop-blur-md md:p-5"
										style={{
											WebkitBackdropFilter: 'blur(12px)',
										}}
									>
										<div className="flex items-start justify-between gap-3">
											<h2 className="text-left text-base font-bold leading-snug text-zinc-900 md:text-lg">
												{dim.title}
											</h2>
											<span
												className={cn(
													'shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wide',
													styles.badge,
												)}
											>
												{styles.label}
											</span>
										</div>

										<div className="mt-3 flex items-baseline gap-1">
											<span className="text-3xl font-black tabular-nums text-zinc-900">
												{dim.score}
											</span>
											<span className="text-lg font-semibold tabular-nums text-zinc-400">
												/{dim.max}
											</span>
										</div>

										<div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-200">
											<div
												className={cn(
													'h-full rounded-full transition-all duration-500',
													styles.bar,
												)}
												style={{ width: `${pct}%` }}
											/>
										</div>

										<p className="mt-3 text-left text-sm leading-relaxed text-zinc-600">
											{dim.description}
										</p>

										<EnergyDimensionTipBlock
											tip={dim.tip}
											index={index}
											tipHeading={tipHeading}
										/>
									</article>
								);
							})}
						</div>

						<div className="mt-10 flex w-full flex-col items-center gap-4 pb-2">
							<Link
								to="/clubes"
								onClick={() => {
									try {
										sessionStorage.setItem(
											PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
											'1',
										);
									} catch {
										/* ignore */
									}
								}}
								className={cn(
									FORM_WELLBEING_PORTRAIT_CTA_CLASS,
									'inline-flex flex-wrap items-center justify-center gap-2 whitespace-normal text-center no-underline',
								)}
							>
								{unlockPlanCtaLabel}
								<ArrowRight
									className="h-5 w-5 shrink-0"
									strokeWidth={2.5}
									aria-hidden
								/>
							</Link>

							{onDecline ? (
								<button
									type="button"
									onClick={onDecline}
									className={cn(
										DECLINE_OUTLINE_CTA_CLASS,
										'inline-flex cursor-pointer items-center text-center',
									)}
								>
									{declineCtaLabel}
								</button>
							) : (
								<Link
									to={declineRoute}
									onClick={() => {
										try {
											sessionStorage.setItem(
												PLUMAA_ALLOW_HOME_AFTER_FORM_FLOW_KEY,
												'1',
											);
										} catch {
											/* ignore */
										}
									}}
									className={cn(
										DECLINE_OUTLINE_CTA_CLASS,
										'inline-flex items-center text-center no-underline',
									)}
								>
									{declineCtaLabel}
								</Link>
							)}

							<p className="mt-2 max-w-2xl text-center text-xs leading-relaxed text-zinc-500 md:text-sm lg:max-w-3xl">
								{declineCtaLead}
							</p>
						</div>
					</div>
				) : null}
			</div>
		</section>
	);
};

export { FormEnergyDimensionsMock };
